import React, { useEffect, useRef, useState } from 'react';
import { IonButton, IonButtons, IonContent, IonFooter, IonHeader, IonIcon, IonTitle } from '@ionic/react';
import { checkmark, closeOutline, colorPaletteOutline, imageOutline } from 'ionicons/icons';
import { useHistory } from 'react-router';
import imageCompression from 'browser-image-compression';
import IonToolbar from '../../../../../components/Modal/ModalToolbar';
import { alterarConfiguracoesCatalogo, assinaUrl, requestService } from '../../../../../Utils/Services';
import './ConfiguracoesCatalogo.css';

type Props = { empresaId: string; empresa: any; cores: string[]; onClose: () => void; onBusyChange?: (busy: boolean) => void; onSaved: (fields: any) => void };
export default function ConfiguracoesCatalogo({ empresaId, empresa, cores, onClose, onSaved, onBusyChange }: Props) {
  const history = useHistory();
  const [color, setColor] = useState(empresa.color || '');
  const [capa, setCapa] = useState<string | null>(empresa.urlCapaCatalogo || null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(capa);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!file) { setPreview(capa); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file, capa]);

  const selectFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.files?.[0];
    event.target.value = '';
    if (!next) return;
    if (!/^image\/(png|jpe?g|avif|webp)$/i.test(next.type)) { setError('Selecione uma imagem PNG, JPG, WebP ou AVIF.'); return; }
    if (next.size > 5 * 1024 * 1024) { setError('A imagem deve ter até 5 MB.'); return; }
    setError(''); setFile(next);
  };
  const save = async () => {
    setBusy(true); onBusyChange?.(true); setError('');
    try {
      let urlCapaCatalogo = capa;
      if (file) {
        const compressed = await imageCompression(file, { maxSizeMB: 0.5, maxWidthOrHeight: 1600, useWebWorker: true });
        const tipo = compressed.type.split('/')[1].replace('jpeg', 'jpg');
        const signed = await fetch(assinaUrl.url + '?pasta=produtos-catalago&bucket=xeguei-mobile-publico', {
          method: assinaUrl.method, credentials: 'include',
          headers: { token: localStorage.getItem('token') || '', 'Content-Type': 'application/json' },
          body: JSON.stringify({ nome: 'capa-catalogo', tipo })
        });
        if (!signed.ok) throw new Error('Não foi possível preparar o envio da imagem.');
        const data = await signed.json();
        if (!data.Item?.urlAssinado || !data.Item?.urlPublica) throw new Error('Não foi possível preparar o envio da imagem.');
        const uploaded = await fetch(data.Item.urlAssinado, { method: 'PUT', headers: { 'Content-Type': 'image/' + tipo }, body: compressed });
        if (!uploaded.ok) throw new Error('Não foi possível enviar a imagem. Tente novamente.');
        urlCapaCatalogo = data.Item.urlPublica;
        setCapa(urlCapaCatalogo); setFile(null);
      }
      const fields = { ...(color ? { color } : {}), urlCapaCatalogo };
      await new Promise<void>((resolve, reject) => {
        requestService(alterarConfiguracoesCatalogo.url.replace('{id}', encodeURIComponent(empresaId)), {
          method: alterarConfiguracoesCatalogo.method, credentials: 'include',
          headers: [['token', localStorage.getItem('token') || '']], body: JSON.stringify(fields)
        }, history, (resp: any) => {
          if (!resp?.Item?.Attributes) { reject(new Error('Não foi possível salvar as configurações.')); return; }
          resolve();
        }, () => reject(new Error('Não foi possível salvar as configurações. Tente novamente.')));
      });
      onSaved(fields);
    } catch (err: any) { setError(err.message || 'Não foi possível salvar as configurações.'); }
    finally { setBusy(false); onBusyChange?.(false); }
  };
  return <>
    <IonHeader className="ion-no-border"><IonToolbar>
      <IonTitle>Configurações do catálogo</IonTitle>
      <IonButtons slot="end"><IonButton aria-label="Fechar" disabled={busy} onClick={onClose}><IonIcon slot="icon-only" icon={closeOutline} /></IonButton></IonButtons>
    </IonToolbar></IonHeader>
    <IonContent className="configuracoes-catalogo-conteudo"><div className="configuracoes-catalogo">
      <section>
        <div className="configuracoes-catalogo-section-header">
          <span className="configuracoes-catalogo-icon"><IonIcon icon={colorPaletteOutline} aria-hidden="true" /></span>
          <div><h2>Cor do catálogo</h2><p>Personalize os botões e os detalhes do seu catálogo.</p></div>
        </div>
        <div className="configuracoes-catalogo-cores">{cores.map(c => <button type="button" key={c} disabled={busy}
          aria-label={`Selecionar cor ${c}`} aria-pressed={color.toLowerCase() === c.toLowerCase()}
          style={{ backgroundColor: c }} onClick={() => setColor(c)}>
          {color.toLowerCase() === c.toLowerCase() && <IonIcon icon={checkmark} aria-hidden="true" />}
        </button>)}</div>
      </section>
      <section>
        <div className="configuracoes-catalogo-section-header">
          <span className="configuracoes-catalogo-icon"><IonIcon icon={imageOutline} aria-hidden="true" /></span>
          <div><h2>Imagem de capa</h2><p>A foto aparece no topo do catálogo. Sem imagem, o cabeçalho fica compacto.</p></div>
        </div>
        {preview && <img className="configuracoes-catalogo-capa" src={preview} alt="Prévia da imagem de capa" />}
        <p>PNG, JPG, WebP ou AVIF, até 5 MB. Prefira uma foto horizontal; o recorte é centralizado.</p>
        <input aria-label="Arquivo da imagem de capa" hidden ref={input} type="file" accept="image/png,image/jpeg,image/webp,image/avif" onChange={selectFile} />
        <div className="configuracoes-catalogo-acoes">
          <IonButton disabled={busy} fill="outline" onClick={() => input.current?.click()}>{preview ? 'Substituir imagem' : 'Selecionar imagem'}</IonButton>
          {preview && <IonButton disabled={busy} fill="clear" color="danger" onClick={() => { setFile(null); setCapa(null); setError(''); }}>Remover imagem</IonButton>}
        </div>
      </section>
      {error && <p role="alert" className="configuracoes-catalogo-erro">{error}</p>}
    </div></IonContent>
    <IonFooter className="configuracoes-catalogo-footer"><IonToolbar><div className="configuracoes-catalogo-rodape"><IonButton disabled={busy} onClick={save}>{busy ? 'Salvando…' : 'Salvar'}</IonButton></div></IonToolbar></IonFooter>
  </>;
}
